/**
 * Fly-by-wire: the fighter's control laws.
 *
 * ## The stick does not move the elevator
 *
 * That is the whole idea, and it is what makes a fly-by-wire fighter feel unlike anything else in
 * this simulator. On the trainer and the aerobat the stick *is* the elevator: pull half and you
 * get half the available deflection, and what happens next is your problem. Here the stick asks
 * for a **load factor**, and a loop running at 120 Hz works out what deflection produces it at
 * this speed, this altitude and this angle of attack. Full aft stick means "nine g", not "all the
 * elevator" — so the same stick position at 200 kt and at 500 kt produces completely different
 * surface deflections and exactly the same g.
 *
 * Three consequences fall straight out of that, and all three are the point:
 *
 *   - **It cannot be over-g'd.** The command saturates at the airframe limit.
 *   - **It cannot be stalled.** The α limiter is a second command in parallel with the g command,
 *     and the law takes whichever asks for less. At full aft stick below corner speed the α
 *     limiter is the one in charge, which is why a real F-16 sits at 25° α and mushes rather than
 *     departing.
 *   - **It is trimmed everywhere.** A g-command law holds 1 g with the stick centred whatever the
 *     speed, because 1 g is what a centred stick *asks for*. The integrator that removes the
 *     standing error is the auto-trim; there is no trim wheel because there is nothing to trim.
 *
 * ## Why the α limiter is a parallel command and not a clamp
 *
 * The obvious implementation — compute the g demand, then clip α afterwards — does not work,
 * because by the time α is at the limit the g loop is still winding up its error and the moment
 * the aircraft dips back under the limit it slams the stick again. The result is a limit cycle
 * that buzzes at a few hertz and feels like a broken aeroplane.
 *
 * Taking `min(g demand, α demand)` instead means the α channel takes over *smoothly* as the limit
 * approaches, because near the limit it is already asking for less than the g channel is. This is
 * the standard structure and the same one the aerobat's test pilot uses in its gate — the
 * difference being that there it is a test harness, and here it is the aeroplane.
 *
 * ## Determinism
 *
 * The law is a plain function of the pilot's commands and the previous tick's state, with two
 * scalar integrator states. No randomness, no wall-clock time, no allocation — so it replays
 * identically and can be run on both ends of a network link (non-negotiables #3 and #7).
 *
 * `updateFixed(dt)`: yes, at 120 Hz, from inside the FDM's own tick.
 */

import { DEG2RAD, clamp } from '../core/math.js';
import type { ControlInputs } from './fdm.js';

/** Sea-level ISA density [kg/m³] — indicated airspeed is defined against it. */
const SEA_LEVEL_DENSITY_KG_M3 = 1.225;

/**
 * The dynamic pressure the pitch law is tuned at [Pa] — about 370 KIAS, the fighter's corner
 * speed, which is where its 9 g validation gate is flown.
 *
 * Gains are scheduled to be exactly the configured values at and below this and to fall as `1/q̄`
 * above it, so the tuned region is bit-for-bit unchanged and only the excess gain at high speed
 * is removed. `0.5 · 1.225 · (370 · 0.5144)²`.
 */
const DESIGN_DYNAMIC_PRESSURE_PA = 22_180;

export interface FlyByWireConfig {
  /** Load factor at full aft stick [g]. */
  maxCommandedG: number;
  /** Load factor at full forward stick [g]. Negative. */
  minCommandedG: number;
  /** Angle-of-attack limit the law will not command past [deg]. */
  alphaLimitDeg: number;
  /** Roll rate at full lateral stick [deg/s]. */
  maxRollRateDegPerS: number;
  /** Proportional gain on load-factor error [command per g]. */
  pitchGain: number;
  /** Damping on the pitch rate the manoeuvre does not need [command per rad/s]. */
  pitchRateGain: number;
  /** Auto-trim integrator gain [command per g per second]. */
  trimIntegralGain: number;
  /** How much of the pitch command the auto-trim integrator may contribute, 0…1. */
  trimAuthority: number;
  /** Proportional gain on the α limiter [command per rad]. */
  alphaGain: number;
  /** Proportional gain on roll-rate error [command per rad/s]. */
  rollGain: number;
  /** Yaw damper gain on yaw rate [command per rad/s]. */
  yawDamperGain: number;
  /** Aileron–rudder interconnect: rudder per unit roll command, for a coordinated roll. */
  aileronRudderInterconnect: number;
  /**
   * Indicated airspeed below which the law blends from g-command to pitch-rate command [kt].
   *
   * A g command is meaningless at low speed: at 100 kt the aeroplane cannot produce 3 g whatever
   * the elevator does, so a loop chasing one simply parks the stick on the stop and the pilot has
   * no pitch control at all on the approach. Below this speed the same stick becomes a pitch-rate
   * command, which is what every real fly-by-wire fighter does and is why the landing flare works.
   */
  gCommandBlendKias: number;
}

/** Standard gravity [m/s²]. */
const G0 = 9.80665;
/** Knots to metres per second, for the blend threshold. */
const MPS_PER_KT = 0.514444;

/**
 * What the law needs to know about the airframe this tick.
 *
 * A struct rather than the `FlightState` itself, because the law is deliberately given only what
 * a real flight control computer has: rates from the gyros, load factor from the accelerometers,
 * α from the vanes and airspeed from the pitot. Handing it the whole simulation state would make
 * it very easy to write a law that cheats, and impossible to notice.
 */
export interface FlyByWireFeedback {
  /** Load factor [g]. */
  loadFactor: number;
  /** Angle of attack [rad]. */
  alphaRad: number;
  /** Sideslip [rad]. */
  betaRad: number;
  /** Body roll rate [rad/s]. */
  rollRateRadPerS: number;
  /** Body pitch rate [rad/s]. */
  pitchRateRadPerS: number;
  /** Body yaw rate [rad/s]. */
  yawRateRadPerS: number;
  /** True airspeed [m/s]. */
  trueAirspeedMps: number;
  /** Indicated airspeed [m/s]. */
  indicatedAirspeedMps: number;
  /** Are the wheels on the ground? */
  onGround: boolean;
}

export function makeFlyByWireFeedback(): FlyByWireFeedback {
  return {
    loadFactor: 1,
    alphaRad: 0,
    betaRad: 0,
    rollRateRadPerS: 0,
    pitchRateRadPerS: 0,
    yawRateRadPerS: 0,
    trueAirspeedMps: 0,
    indicatedAirspeedMps: 0,
    onGround: true,
  };
}

/** What the law is doing right now, for the HUD and for the tests. */
export interface FlyByWireDiagnostics {
  /** Load factor the stick is asking for [g]. */
  commandedG: number;
  /** Roll rate the stick is asking for [rad/s]. */
  commandedRollRateRadPerS: number;
  /** Is the α limiter the channel in charge? */
  alphaLimited: boolean;
  /** The auto-trim integrator's current contribution to the pitch command. */
  trimCommand: number;
  /** How much of the law is g-command rather than pitch-rate command, 0…1. */
  gCommandBlend: number;
  /**
   * Gain-schedule factor actually applied this tick, 0…1.
   *
   * 1 at and below the design corner speed, falling as `1/q̄` above it. Exposed so a test can
   * assert the tuned region is untouched rather than infer it.
   */
  gainScale: number;
}

export class FlyByWire {
  private readonly config: FlyByWireConfig;
  private readonly alphaLimitRad: number;
  private readonly maxRollRateRadPerS: number;
  private readonly blendSpeedMps: number;

  /** The auto-trim integrator. One number, and it is the entire reason there is no trim wheel. */
  private trimIntegral = 0;

  readonly diagnostics: FlyByWireDiagnostics = {
    commandedG: 1,
    commandedRollRateRadPerS: 0,
    alphaLimited: false,
    trimCommand: 0,
    gCommandBlend: 1,
    gainScale: 1,
  };

  constructor(config: FlyByWireConfig) {
    this.config = config;
    this.alphaLimitRad = config.alphaLimitDeg * DEG2RAD;
    this.maxRollRateRadPerS = config.maxRollRateDegPerS * DEG2RAD;
    this.blendSpeedMps = config.gCommandBlendKias * MPS_PER_KT;
  }

  /** Forget the integrator, e.g. after a respawn. */
  reset(): void {
    this.trimIntegral = 0;
  }

  /**
   * Turn the pilot's stick into surface commands.
   *
   * Writes into `out` and returns it; allocates nothing. `out` may not alias `pilot`.
   */
  updateFixed(
    pilot: Readonly<ControlInputs>,
    feedback: Readonly<FlyByWireFeedback>,
    dtSeconds: number,
    out: ControlInputs,
  ): ControlInputs {
    const config = this.config;
    const diagnostics = this.diagnostics;

    // Everything the law does not touch passes straight through.
    out.throttle = pilot.throttle;
    out.flapDetent = pilot.flapDetent;
    out.brakeLeft = pilot.brakeLeft;
    out.brakeRight = pilot.brakeRight;
    out.parkingBrake = pilot.parkingBrake;
    out.gearDown = pilot.gearDown;
    out.speedbrake = pilot.speedbrake;
    out.ignition = pilot.ignition;
    out.starter = pilot.starter;
    // There is no trim wheel on a g-command aeroplane: the integrator below is the trim.
    out.pitchTrim = 0;
    out.yawTrim = 0;
    out.rollTrim = 0;
    out.steer = pilot.yaw;

    /*
     * On the ground the law stands down.
     *
     * A g command with the wheels on the runway asks for a load factor the gear is already
     * providing, so the loop would wind its integrator up against the oleos and hold full aft
     * stick through the take-off roll. Below flying speed there is nothing for a g command to
     * mean, so the stick goes back to being a stick — which is also what a pilot expects when
     * rotating.
     */
    const pitchStick = clamp(pilot.pitch, -1, 1);
    const rollStick = clamp(pilot.roll, -1, 1);
    const yawStick = clamp(pilot.yaw, -1, 1);

    if (feedback.onGround || feedback.indicatedAirspeedMps < this.blendSpeedMps * 0.35) {
      this.trimIntegral = 0;
      out.pitch = pitchStick;
      out.roll = rollStick;
      out.yaw = yawStick;
      diagnostics.commandedG = 1;
      diagnostics.commandedRollRateRadPerS = 0;
      diagnostics.alphaLimited = false;
      diagnostics.trimCommand = 0;
      diagnostics.gCommandBlend = 0;
      return out;
    }

    // --- pitch: g command, blended to a pitch-rate command at low speed --------------------
    const commandedG =
      pitchStick >= 0
        ? 1 + pitchStick * (config.maxCommandedG - 1)
        : 1 + pitchStick * (1 - config.minCommandedG);
    diagnostics.commandedG = commandedG;

    /*
     * The pitch rate a *correct* answer would already have.
     *
     * `q = g·√(n²−1)/V` is the body pitch rate of a steady pull at `n` g. Damping on raw `q`
     * instead would subtract from the command in proportion to how well the manoeuvre is going,
     * which drives the elevator down at the moment of highest demand and stops the law reaching
     * its own commanded g. Referencing the damping here leaves it damping only the overshoot.
     */
    /*
     * Gain scheduling on dynamic pressure — the thing this law was missing.
     *
     * A control surface's moment scales with q̄, so a *fixed* gain makes the loop gain scale with
     * q̄ too. Above some speed the discrete 120 Hz loop no longer has the phase margin to carry it
     * and the aeroplane rings. Measured hands-off in level flight at 4,134 ft, before this:
     *
     * ```
     *   KIAS   g swing (FBW on)   g swing (FBW removed)
     *    440        0.040                0.017
     *    480        4.10                 0.015     <-- and the airframe alone is fine
     *    560        6.57                 0.011
     * ```
     *
     * The airframe is stable at every speed; the *loop* is not. A pilot reported it as jitter
     * starting "around 480", in every camera view, on the only aircraft in the fleet that has
     * fly-by-wire — which in hindsight was the tell.
     *
     * Real flight control systems schedule gain against q̄ (or Mach and altitude) for exactly this
     * reason. `DESIGN_DYNAMIC_PRESSURE_PA` is the corner speed the law was tuned at, ~370 KIAS, so
     * the scale is **1 at and below the design point** and falls as `1/q̄` above it. That leaves
     * the tuned region — where the 9 g corner-speed gate lives — untouched, and only removes the
     * excess gain that the aeroplane could not have used anyway.
     *
     * Sea-level-equivalent q̄ is what indicated airspeed measures, so IAS is the right input and no
     * atmosphere lookup is needed here.
     */
    const dynamicPressurePa =
      0.5 * SEA_LEVEL_DENSITY_KG_M3 * feedback.indicatedAirspeedMps * feedback.indicatedAirspeedMps;
    const gainScale = Math.min(1, DESIGN_DYNAMIC_PRESSURE_PA / Math.max(1, dynamicPressurePa));
    const pitchGain = config.pitchGain * gainScale;
    const pitchRateGain = config.pitchRateGain * gainScale;
    const alphaGain = config.alphaGain * gainScale;
    diagnostics.gainScale = gainScale;

    const requiredPitchRate =
      (G0 * Math.sqrt(Math.max(0, commandedG * commandedG - 1))) /
      Math.max(1, feedback.trueAirspeedMps);
    const gError = commandedG - feedback.loadFactor;
    const gDemand =
      gError * pitchGain -
      (feedback.pitchRateRadPerS - requiredPitchRate) * pitchRateGain +
      this.trimIntegral;

    // The α channel, running in parallel. Whichever asks for less is in charge.
    const alphaDemand =
      (this.alphaLimitRad - feedback.alphaRad) * alphaGain -
      feedback.pitchRateRadPerS * pitchRateGain;

    // …and the low-speed law, a straight pitch-rate command, blended in below the threshold.
    const blend = clamp(feedback.indicatedAirspeedMps / Math.max(1, this.blendSpeedMps), 0, 1);
    diagnostics.gCommandBlend = blend;
    const rateDemand = pitchStick - feedback.pitchRateRadPerS * pitchRateGain;

    const limited = Math.min(gDemand, alphaDemand);
    diagnostics.alphaLimited = alphaDemand < gDemand;
    const pitchCommand = clamp(limited * blend + rateDemand * (1 - blend), -1, 1);
    out.pitch = pitchCommand;

    /*
     * Auto-trim.
     *
     * Integrate the load-factor error, but only while the command is not saturated and the α
     * limiter is not in charge — otherwise the integrator winds up against a limit it cannot
     * beat and takes seconds to unwind when the pilot relaxes, which reads as the aeroplane
     * fighting the stick. This is textbook anti-windup and it is the one piece of a g-command
     * law that is always more subtle than it looks.
     */
    const saturated = Math.abs(pitchCommand) >= 0.999;
    if (!saturated && !diagnostics.alphaLimited && blend > 0.5) {
      this.trimIntegral = clamp(
        this.trimIntegral + gError * config.trimIntegralGain * dtSeconds,
        -config.trimAuthority,
        config.trimAuthority,
      );
    }
    diagnostics.trimCommand = this.trimIntegral;

    // --- roll: a rate command ------------------------------------------------------------
    // Full lateral stick asks for the airframe's maximum roll rate. Between the stops the pilot
    // is choosing a rate, not a deflection, which is why a fly-by-wire fighter rolls the same
    // amount for the same stick at any speed.
    const commandedRollRate = rollStick * this.maxRollRateRadPerS;
    diagnostics.commandedRollRateRadPerS = commandedRollRate;
    out.roll = clamp((commandedRollRate - feedback.rollRateRadPerS) * config.rollGain, -1, 1);

    // --- yaw: damper plus interconnect ------------------------------------------------------
    // The pedals stay live for a pilot who wants them, with a yaw damper underneath to kill the
    // Dutch roll and an aileron–rudder interconnect so a roll comes out coordinated without any.
    out.yaw = clamp(
      yawStick -
        feedback.yawRateRadPerS * config.yawDamperGain +
        rollStick * config.aileronRudderInterconnect,
      -1,
      1,
    );

    return out;
  }
}
